import React from 'react';
import { cn } from '@/lib/utils';

// --- Base Skeleton Component ---
// This is the core component with the shimmer animation.
// It can be used directly or composed into more complex skeletons.

const Skeleton = ({ className, ...props }) => {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-muted', className)}
      {...props}
    />
  );
};

// --- Composable Skeleton Variants ---
// These are pre-built, common skeleton structures for convenience.

/**
 * A skeleton loader for a block of text.
 * @param {object} props - The component props.
 * @param {number} [props.lines=3] - The number of text lines to render.
 * @param {string} [props.className] - Additional classes for the container.
 * @param {string} [props.lineClassName] - Additional classes for each line.
 */
const SkeletonText = ({ lines = 3, className, lineClassName }) => {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn(
            'h-4',
            // Make lines progressively shorter for a more realistic look
            i === 0 && 'w-full',
            i > 0 && i < lines - 1 && 'w-11/12',
            i === lines - 1 && lines > 1 && 'w-4/5',
            lineClassName
          )}
        />
      ))}
    </div>
  );
};

/**
 * A skeleton loader for a card component.
 * @param {object} props - The component props.
 * @param {boolean} [props.hasImage=true] - Whether the card has an image placeholder.
 * @param {boolean} [props.hasAvatar=false] - Whether to show an avatar in the header.
 * @param {string} [props.className] - Additional classes for the card container.
 */
const SkeletonCard = ({ hasImage = true, hasAvatar = false, className }) => {
  return (
    <div className={cn('p-4 border rounded-lg space-y-4 bg-background', className)}>
      {hasImage && <Skeleton className="h-32 w-full rounded-md" />}
      <div className="space-y-3">
        <div className="flex items-center space-x-4">
          {hasAvatar && <Skeleton className="h-12 w-12 rounded-full" />}
          <div className="space-y-2 flex-1">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
        <SkeletonText lines={2} />
        <Skeleton className="h-8 w-24 mt-2" />
      </div>
    </div>
  );
};

/**
 * A skeleton loader for a list of items.
 * @param {object} props - The component props.
 * @param {number} [props.count=3] - The number of list items to render.
 * @param {string} [props.className] - Additional classes for the container.
 */
const SkeletonList = ({ count = 3, className }) => {
  return (
    <div className={cn('space-y-4', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center space-x-4 p-2 border-b">
          <Skeleton className="h-10 w-10 rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
          <Skeleton className="h-6 w-12" />
        </div>
      ))}
    </div>
  );
};


// --- Specific Skeleton Loaders ---
// Example of a page-specific skeleton loader.

const DashboardSkeleton = () => {
  return (
    <div className="p-4 md:p-8 space-y-6">
      <header className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32" />
      </header>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <SkeletonCard hasImage={false} hasAvatar={true} />
        <SkeletonCard hasImage={false} hasAvatar={true} />
        <SkeletonCard hasImage={false} hasAvatar={true} />
        <SkeletonCard hasImage={false} hasAvatar={true} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-4">
           <Skeleton className="h-6 w-40 mb-2" />
           <SkeletonCard hasImage={false} />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-6 w-40 mb-2" />
          <SkeletonList count={4} />
        </div>
      </div>
    </div>
  );
};


// --- Invoice List Skeleton ---
const InvoiceListSkeleton = ({ count = 10 }) => {
  return (
    <div className="space-y-1" data-testid="invoice-list-skeleton">
      {/* Header */}
      <div className="grid grid-cols-6 gap-4 p-4 border-b font-semibold">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-16" />
      </div>

      {/* Rows */}
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="grid grid-cols-6 gap-4 p-4 border-b items-center hover:bg-muted/50">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </div>
  );
};

// --- Customer List Skeleton ---
const CustomerListSkeleton = ({ count = 10 }) => {
  return (
    <div className="space-y-1" data-testid="customer-list-skeleton">
      {/* Header */}
      <div className="grid grid-cols-5 gap-4 p-4 border-b font-semibold">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-16" />
      </div>

      {/* Rows */}
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="grid grid-cols-5 gap-4 p-4 border-b items-center hover:bg-muted/50">
          <div className="flex items-center space-x-3">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </div>
  );
};

// --- Payment List Skeleton ---
const PaymentListSkeleton = ({ count = 10 }) => {
  return (
    <div className="space-y-1" data-testid="payment-list-skeleton">
      {/* Header */}
      <div className="grid grid-cols-6 gap-4 p-4 border-b font-semibold">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-16" />
      </div>

      {/* Rows */}
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="grid grid-cols-6 gap-4 p-4 border-b items-center hover:bg-muted/50">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-6 w-16 rounded-full" />
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </div>
  );
};

// --- Analytics Chart Skeleton ---
const AnalyticsChartSkeleton = () => {
  return (
    <div className="space-y-4" data-testid="analytics-chart-skeleton">
      <div className="flex justify-between items-center">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Chart area */}
      <div className="border rounded-lg p-6">
        <div className="flex items-end justify-between h-64 space-x-2">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton
              key={i}
              className="w-full rounded-t-md"
              style={{ height: `${Math.random() * 80 + 20}%` }}
            />
          ))}
        </div>

        {/* Legend */}
        <div className="flex justify-center space-x-6 mt-4">
          <div className="flex items-center space-x-2">
            <Skeleton className="h-3 w-3 rounded-full" />
            <Skeleton className="h-3 w-20" />
          </div>
          <div className="flex items-center space-x-2">
            <Skeleton className="h-3 w-3 rounded-full" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Landing Page Skeleton ---
// For landing and marketing pages - full-width, hero-style layout
const LandingSkeleton = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Skeleton className="h-8 w-32" />
          <div className="hidden md:flex items-center space-x-6">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-9 w-24 rounded-md" />
          </div>
          <Skeleton className="h-8 w-8 md:hidden" />
        </div>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-20 flex flex-col items-center text-center">
        <Skeleton className="h-6 w-64 mb-6 rounded-full" />
        <Skeleton className="h-12 w-full max-w-lg mb-4" />
        <Skeleton className="h-10 w-full max-w-md mb-2" />
        <Skeleton className="h-6 w-full max-w-sm mb-8" />
        <div className="flex space-x-4">
          <Skeleton className="h-12 w-36 rounded-md" />
          <Skeleton className="h-12 w-32 rounded-md" />
        </div>
      </div>

      {/* Features hint */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-center space-x-8">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
    </div>
  );
};

// --- Auth Page Skeleton ---
// For auth pages - centered card with form fields
const AuthSkeleton = () => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="flex justify-center">
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Card */}
        <div className="border rounded-lg p-8 space-y-6 bg-card">
          {/* Title */}
          <div className="text-center space-y-2">
            <Skeleton className="h-7 w-40 mx-auto" />
            <Skeleton className="h-4 w-56 mx-auto" />
          </div>

          {/* Form fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-12 w-full rounded-md" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-12 w-full rounded-md" />
            </div>
          </div>

          {/* Button */}
          <Skeleton className="h-12 w-full rounded-md" />

          {/* Divider */}
          <div className="flex items-center space-x-4">
            <Skeleton className="h-px flex-1" />
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-px flex-1" />
          </div>

          {/* OAuth button */}
          <Skeleton className="h-12 w-full rounded-md" />
        </div>

        {/* Footer link */}
        <Skeleton className="h-4 w-48 mx-auto" />
      </div>
    </div>
  );
};

// --- Table Skeleton (Reusable) ---
const TableSkeleton = ({ rows = 10, columns = 5 }) => {
  return (
    <div className="space-y-1" data-testid="table-skeleton">
      {/* Header */}
      <div className={`grid gap-4 p-4 border-b font-semibold`} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-4 w-20" />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          className={`grid gap-4 p-4 border-b items-center hover:bg-muted/50`}
          style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <Skeleton key={colIdx} className="h-4 w-24" />
          ))}
        </div>
      ))}
    </div>
  );
};

// --- Settings Page Skeleton ---
const SettingsSkeleton = () => {
  return (
    <div className="p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-4 w-64" />
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b pb-2">
        <Skeleton className="h-9 w-24 rounded-md" />
        <Skeleton className="h-9 w-24 rounded-md" />
        <Skeleton className="h-9 w-24 rounded-md" />
        <Skeleton className="h-9 w-24 rounded-md" />
      </div>

      {/* Settings form sections */}
      <div className="space-y-6">
        <div className="border rounded-lg p-6 space-y-4">
          <Skeleton className="h-6 w-40" />
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full rounded-md" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-10 w-full rounded-md" />
            </div>
          </div>
        </div>

        <div className="border rounded-lg p-6 space-y-4">
          <Skeleton className="h-6 w-40" />
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between py-2">
                <div className="space-y-1">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-60" />
                </div>
                <Skeleton className="h-6 w-12 rounded-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Invoices Page Skeleton (Full Page) ---
const InvoicesPageSkeleton = () => {
  return (
    <div className="p-4 md:p-8 space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-4 w-48" />
        </div>
        <div className="flex space-x-2">
          <Skeleton className="h-10 w-32 rounded-md" />
          <Skeleton className="h-10 w-28 rounded-md" />
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="border rounded-lg p-4 space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex space-x-2">
        <Skeleton className="h-10 w-64 rounded-md" />
        <Skeleton className="h-10 w-32 rounded-md" />
        <Skeleton className="h-10 w-28 rounded-md" />
      </div>

      {/* Table */}
      <InvoiceListSkeleton count={8} />
    </div>
  );
};

// --- Customers Page Skeleton (Full Page) ---
const CustomersPageSkeleton = () => {
  return (
    <div className="p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-36" />
          <Skeleton className="h-4 w-52" />
        </div>
        <Skeleton className="h-10 w-36 rounded-md" />
      </div>

      {/* Search and filters */}
      <div className="flex space-x-2">
        <Skeleton className="h-10 w-72 rounded-md" />
        <Skeleton className="h-10 w-28 rounded-md" />
      </div>

      {/* Table */}
      <CustomerListSkeleton count={8} />
    </div>
  );
};

// --- Route-Aware Loading Component ---
// Automatically selects the appropriate skeleton based on route type
const RouteAwareLoading = ({ pathname }) => {
  // Auth routes
  if (pathname?.startsWith('/auth') || pathname?.startsWith('/super-admin') ||
      pathname === '/onboarding' || pathname === '/login-debug' || pathname === '/auth-test') {
    return <AuthSkeleton />;
  }

  // Landing/marketing routes
  if (pathname === '/' || pathname === '/pricing' || pathname === '/features' || pathname === '/about' ||
      pathname === '/contact' || pathname === '/privacy' || pathname === '/terms' || pathname === '/cookies' ||
      pathname === '/gdpr' || pathname?.startsWith('/invoice/') || pathname?.startsWith('/portal/')) {
    return <LandingSkeleton />;
  }

  // All protected app routes default to DashboardSkeleton
  return <DashboardSkeleton />;
};

export {
  Skeleton,
  SkeletonText,
  SkeletonCard,
  SkeletonList,
  DashboardSkeleton,
  LandingSkeleton,
  AuthSkeleton,
  InvoiceListSkeleton,
  CustomerListSkeleton,
  PaymentListSkeleton,
  AnalyticsChartSkeleton,
  TableSkeleton,
  SettingsSkeleton,
  InvoicesPageSkeleton,
  CustomersPageSkeleton,
  RouteAwareLoading
};
